import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Banknote, CreditCard, Landmark, ReceiptText, WalletCards } from 'lucide-react';
import PageHeader from '../../components/shared/PageHeader';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { EmptyResults, ErrorState, LoadingState } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { listPayments } from '../../api/hostApi';

const money = (minor, currency = 'USD') => {
  const value = Number(minor ?? 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch (_) {
    return `$${value.toFixed(2)}`;
  }
};

const date = (value) => value
  ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—';

export default function Payments() {
  const { data, loading, error, refetch } = useApi(listPayments, []);
  const summary = data?.summary || {};
  const items = useMemo(() => data?.items || [], [data]);

  return (
    <div>
      <PageHeader
        title="Payments & payouts"
        description="Booking revenue, StayEasy fees, Stripe processing costs, and your net transfers."
        actions={(
          <Link to="/host/payout-setup" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <Landmark className="h-4 w-4" /> Payout setup
          </Link>
        )}
      />

      {loading ? <LoadingState label="Loading payments…" />
        : error ? <ErrorState error={error} onRetry={refetch} />
        : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
              <Metric icon={Banknote} label="Gross bookings" value={money(summary.gross_amount_minor)} />
              <Metric icon={ReceiptText} label="StayEasy platform fee" value={money(summary.platform_fee_amount_minor)} note="12% of booking gross" />
              <Metric icon={CreditCard} label="Stripe processing" value={money(summary.stripe_fee_amount_minor)} note="Actual Stripe fees" />
              <Metric icon={WalletCards} label="Net transferred to you" value={money(summary.host_net_amount_minor)} />
            </div>

            <Card className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-slate-900">Transaction history</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{summary.paid_transactions ?? items.length} paid booking transactions</p>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="p-5"><EmptyResults icon={WalletCards} title="No payments yet" description="Completed guest payments will appear here with their exact fee and payout breakdown." /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-3 text-left font-semibold">Booking</th>
                        <th className="px-4 py-3 text-left font-semibold">Paid</th>
                        <th className="px-4 py-3 text-right font-semibold">Gross</th>
                        <th className="px-4 py-3 text-right font-semibold">StayEasy 12%</th>
                        <th className="px-4 py-3 text-right font-semibold">Stripe fee</th>
                        <th className="px-4 py-3 text-right font-semibold">Host net</th>
                        <th className="px-5 py-3 text-left font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((transaction) => {
                        const booking = transaction.booking || {};
                        const property = booking.property || {};
                        const guest = booking.guest || {};
                        return (
                          <tr key={transaction.id} className="hover:bg-slate-50/70">
                            <td className="px-5 py-4">
                              <div className="font-semibold text-slate-900">{property.title || `Booking #${booking.id || '—'}`}</div>
                              <div className="text-xs text-slate-500 mt-0.5">Booking #{booking.id || '—'} · {guest.name || guest.email || 'Guest'}</div>
                              <div className="text-[11px] text-slate-400 mt-1">{transaction.transaction_id || transaction.stripe_transfer_id || 'Stripe settlement'}</div>
                            </td>
                            <td className="px-4 py-4 text-slate-600 whitespace-nowrap">{date(transaction.paid_at)}</td>
                            <td className="px-4 py-4 text-right font-medium tabular-nums text-slate-900">{money(transaction.gross_amount_minor, transaction.currency)}</td>
                            <td className="px-4 py-4 text-right tabular-nums text-slate-600">− {money(transaction.platform_fee_amount_minor, transaction.currency)}</td>
                            <td className="px-4 py-4 text-right tabular-nums text-slate-600">− {money(transaction.stripe_fee_amount_minor, transaction.currency)}</td>
                            <td className="px-4 py-4 text-right font-bold tabular-nums text-emerald-700">{money(transaction.host_net_amount_minor, transaction.currency)}</td>
                            <td className="px-5 py-4"><StatusPill status={transaction.status || 'paid'} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, note }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-50 text-slate-600 grid place-items-center"><Icon className="h-5 w-5" /></div>
      </div>
      <div className="mt-4 text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
      <div className="mt-1 text-xs font-medium text-slate-600">{label}</div>
      {note && <div className="mt-1 text-[11px] text-slate-400">{note}</div>}
    </Card>
  );
}
